import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import PropertyCard from "./PropertyCard";

import {
  fetchProperties,
  togglePropertyLike,
  togglePropertyBookmark,
} from "../api/realty";

// RealtySavedScreenContent — /saved canonical bookmarks grid.
//
// Substrate: design/saved.html #Csection (.saved-section). Per
// design-map.md §13: "minimal composition: title → grid. No
// aggregation, no filtering." Card carries the .card-flexible
// structural modifier (no CSS rules in modernize.css — preserved as
// a structural marker only).
//
// Backend reconciliation (mini-phase):
//   * Hydrates via ?bookmarked_by=me — canonical self-scope on the
//     existing list endpoint. Reload-safe, new-tab-safe, cross-
//     device-safe (truth lives in PropertyBookmark on the backend,
//     not in client memory).
//   * Engagement Sets are hydrated from liked_by_me /
//     bookmarked_by_me on each fetch; they remain route-local
//     optimistic overlays for click-time immediacy.
//   * Un-bookmark drops the row from the local results array as
//     well as the Set, so the card disappears immediately without
//     refetch. The next fetch will agree.

function RealtySavedScreenContent() {
  const userInfo = useSelector(
    (state) => state.userLogin && state.userLogin.userInfo
  );
  const currentUserId =
    (userInfo && (userInfo.id || userInfo.public_id)) || null;
  const navigate = useNavigate();

  // R8.2.7 — screen-local anon-entry guard. The bootstrap gate in App.js
  // prevents pre-hydration mount, but direct entry to /saved without a
  // session still relies on the axios 401 → coordinator → performLogout
  // cascade to redirect. That cascade works but wastes one round-trip
  // and renders a Spinner flash. Local redirect eliminates both.
  // Note: no foundation modification — coordinator still handles
  // session-mid-mutation paths.
  useEffect(() => {
    if (!userInfo) {
      navigate("/login", { replace: true });
    }
  }, [userInfo, navigate]);

  const [results, setResults] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  // Engagement Sets — route-local optimistic overlays. Hydrated
  // from backend on every fetch; updated immediately on toggle
  // responses.
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [likedIds, setLikedIds] = useState(new Set());
  const [likingIds, setLikingIds] = useState(new Set());
  const [bookmarkingIds, setBookmarkingIds] = useState(new Set());
  const likingIdsRef = useRef(new Set());
  const bookmarkingIdsRef = useRef(new Set());
  const [engagementError, setEngagementError] = useState(null);

  // R5.3 — Share clipboard-copy state. Per-card "copied" indicator;
  // mirrors the R3.10A copy-link containment philosophy from
  // DetailActionsPanel.js. No timer registry, no cleanup ref — the
  // setTimeout callback uses the functional updater so a setState
  // after unmount is a silent no-op.
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!userInfo) return undefined;
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchProperties({ bookmarked_by: "me" })
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.results) ? data.results : []);
        setResults(rows);
        setLikedIds(
          new Set(rows.filter((r) => r.liked_by_me).map((r) => r.id))
        );
        // Every row on /saved is bookmarked by construction (backend
        // filter guarantees it); the Set is initialized accordingly.
        setBookmarkedIds(new Set(rows.map((r) => r.id)));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleLike = async (property) => {
    if (!property || likingIdsRef.current.has(property.id)) return;
    likingIdsRef.current.add(property.id);
    setLikingIds(new Set(likingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyLike(property.id);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (data && data.liked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      likingIdsRef.current.delete(property.id);
      setLikingIds(new Set(likingIdsRef.current));
    }
  };

  // R5.3 — Share is a surface-local clipboard side effect. Copies the
  // canonical property URL only; NO PropertyShare token, NO referral,
  // NO analytics, NO cross-surface sync. Failures (Clipboard API
  // unavailable / write rejected) are silent per STEP R5.3.7.
  const handleShareCopy = async (property) => {
    const url = `${window.location.origin}/realty/${property.id}`;
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== "function"
      ) {
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopiedId(property.id);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === property.id ? null : curr));
      }, 2000);
    } catch (_err) {
      /* silent — clipboard write failed */
    }
  };

  const handleToggleBookmark = async (property) => {
    if (!property || bookmarkingIdsRef.current.has(property.id)) return;
    bookmarkingIdsRef.current.add(property.id);
    setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyBookmark(property.id);
      const stillBookmarked = Boolean(data && data.bookmarked);
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (stillBookmarked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
      // /saved-specific behavior: un-bookmark removes the row from
      // the local results so the card disappears immediately. The
      // backend would exclude it on the next fetch anyway.
      if (!stillBookmarked) {
        setResults((prev) => prev.filter((r) => r.id !== property.id));
      }
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      bookmarkingIdsRef.current.delete(property.id);
      setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    }
  };

  const buildMenu = (property) => {
    const ownerId =
      (property.created_by && property.created_by.id) ||
      (property.created_by && property.created_by.public_id);
    const isOwner =
      currentUserId !== null &&
      currentUserId !== undefined &&
      ownerId !== undefined &&
      String(ownerId) === String(currentUserId);

    if (isOwner) {
      // Share + Edit. Lifecycle Sold/Delete live on profile/detail/edit
      // (ACTION-MENUS-UX-B1-01).
      return [
        {
          id: "share",
          label:
            copiedId === property.id
              ? "Посилання скопійовано"
              : "Поширити",
          icon: "fa-solid fa-share-nodes",
          kind: "button",
          // Keep the modal open so the inline label swap is visible.
          // User dismisses manually via backdrop / Escape.
          dismiss: false,
          onClick: () => handleShareCopy(property),
        },
        {
          id: "edit",
          label: "Редагувати",
          icon: "fa-solid fa-pen-to-square",
          kind: "link",
          // R6.1 — wired to the editRealty surface. Owner-gated at
          // the screen level via R3.9 ownership derivation.
          href: `/realty/${property.id}/edit`,
        },
      ];
    }

    // Viewer list-card menu: Share only. Follow/complaint live on
    // Property detail (ACTION-MENUS-UX-B1-01).
    return [
      {
        id: "share",
        label:
          copiedId === property.id
            ? "Посилання скопійовано"
            : "Поширити",
        icon: "fa-solid fa-share-nodes",
        kind: "button",
        // Keep the modal open so the inline label swap is visible.
        // User dismisses manually via backdrop / Escape.
        dismiss: false,
        onClick: () => handleShareCopy(property),
      },
    ];
  };

  return (
    <div className="container">
      <div className="w-100 d-flex justify-content-center">
        <h2>Збереженні</h2>
      </div>

      {engagementError ? (
        <div className="feature-realty-list__engagement-error" role="alert">
          {engagementError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-realty-list__state">
          <Spinner size="lg" label="Завантаження збережених" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-realty-list__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити збережені"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
          />
        </div>
      ) : results.length === 0 ? (
        <div className="feature-realty-list__state">
          <EmptyState
            icon="fa-regular fa-bookmark"
            title="Поки що немає збережених оголошень"
            description="Натисніть позначку закладки на оголошенні, щоб додати його сюди."
            size="md"
          />
        </div>
      ) : (
        <div className="row" id="cards-content">
          {results.map((property) => {
            const detailHref = `/realty/${property.id}`;
            const ownerHandle =
              property.created_by &&
              (property.created_by.handle ||
                property.created_by.public_id);
            const ownerHref = ownerHandle ? `/user/${ownerHandle}` : null;
            return (
              <div
                key={property.id}
                className="card-elem col-12 col-lg-6 col-xxl-4"
              >
                <PropertyCard
                  property={property}
                  flexible
                  detailHref={detailHref}
                  ownerHref={ownerHref}
                  liked={likedIds.has(property.id)}
                  bookmarked={bookmarkedIds.has(property.id)}
                  likePending={likingIds.has(property.id)}
                  bookmarkPending={bookmarkingIds.has(property.id)}
                  onToggleLike={() => handleToggleLike(property)}
                  onToggleBookmark={() => handleToggleBookmark(property)}
                  menuItems={buildMenu(property)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RealtySavedScreenContent;
